/**
 * Utility functions for JWT token handling
 */

/**
 * Decode JWT token without verification (for reading expiration time)
 * @param token JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeJwt(token: string): { exp?: number; iat?: number; [key: string]: any } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Get expiration time from JWT token
 * @param token JWT token string
 * @returns Expiration timestamp in milliseconds, or null if invalid
 */
export function getTokenExpiration(token: string): number | null {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) return null;
  
  // JWT exp is in seconds, convert to milliseconds
  return decoded.exp * 1000;
}

/**
 * Check if token is expired or will expire soon
 * @param token JWT token string
 * @param bufferMinutes Minutes before expiration to consider as "expiring soon" (default: 3)
 * @param nowMs Optional "now" in ms (e.g. from timeProvider in DEV so validation uses same clock as backend)
 * @returns true if token is expired or will expire within buffer time
 */
export function isTokenExpiringSoon(token: string, bufferMinutes: number = 3, nowMs?: number): boolean {
  const expiration = getTokenExpiration(token);
  if (!expiration) return true; // Consider invalid tokens as expired
  
  const now = nowMs ?? Date.now();
  const bufferMs = bufferMinutes * 60 * 1000;
  
  return expiration <= (now + bufferMs);
}
